import { SESSION_ERROR_KEY } from '@constants/index'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { SessionError } from '@typesApp/AppEvent'
import { SubmitErrorResponse } from '@typesApp/SubmitResponse'

const STATUS_CODES = {
  GATEWAY_TIMEOUT: 504,
}

// commerce sends this to indicate session expiry
const EXPIRED_COOKIE_ERROR = 'ERR_COOKIE_EXPIRED'

// other possible commerce (HCL) session expiry errors
const EXPIRED_ACTIVITY_TOKEN_ERROR_CODE = 'CWXBB1011E'
const EXPIRED_ACTIVITY_TOKEN_ERROR_KEY = 'CWXBB1011E'
const ACTIVITY_TOKEN_ERROR_CODE = 'CWXBB1103E'
const ACTIVITY_TOKEN_ERROR_KEY = '_ERR_ACTIVITY_TOKEN'
const PARTIAL_AUTHENTICATION_ERROR_CODE = 'CWXFR0222E'
const PARTIAL_AUTHENTICATION_ERROR_KEY = 'ERR_PARTIAL_AUTHENTICATION_NOT_ALLOWE' // this is not a typo
const EXPIRED_COOKIE_ERROR_CODE = 'CWXFR0223E'

const SESSION_ERROR = {
  CODES: [
    EXPIRED_COOKIE_ERROR,
    EXPIRED_ACTIVITY_TOKEN_ERROR_CODE,
    ACTIVITY_TOKEN_ERROR_CODE,
    PARTIAL_AUTHENTICATION_ERROR_CODE,
    EXPIRED_COOKIE_ERROR_CODE,
    SESSION_ERROR_KEY.SESSION_EXPIRED,
  ],
  KEYS: [
    EXPIRED_COOKIE_ERROR,
    EXPIRED_ACTIVITY_TOKEN_ERROR_KEY,
    ACTIVITY_TOKEN_ERROR_KEY,
    PARTIAL_AUTHENTICATION_ERROR_KEY,
    SESSION_ERROR_KEY.SESSION_EXPIRED,
  ],
}

const CUSTOM_ERROR_STATUS = 'CUSTOM_ERROR'

type FetchErrorData = {
  error?: string
  data?:
    | string
    | {
        error?: string
        message?: string
      }
}

export const isGatewayTimeout = (errorOrStatus: unknown | number | undefined): boolean => {
  return (
    errorOrStatus === STATUS_CODES.GATEWAY_TIMEOUT ||
    (isFetchBaseError(errorOrStatus) && errorOrStatus.status === STATUS_CODES.GATEWAY_TIMEOUT)
  )
}

export const isErrorWithClaimRecord = (error?: unknown): error is SubmitErrorResponse => {
  const errorResponse = error as SubmitErrorResponse
  return Boolean(errorResponse?.status && errorResponse?.data)
}

export const isSessionExpiredError = (error?: unknown): boolean => {
  if (isSessionError(error)) {
    return SESSION_ERROR.CODES.includes(error?.errorCode ?? '') || SESSION_ERROR.KEYS.includes(error?.errorKey ?? '')
  }

  if (isFetchBaseError(error)) {
    return error?.status === CUSTOM_ERROR_STATUS && error?.data === SESSION_ERROR_KEY.SESSION_EXPIRED
  }

  return false
}

export const isSessionTransitionedError = (error?: unknown): boolean => {
  if (isSessionError(error)) {
    return error.errorKey === SESSION_ERROR_KEY.SESSION_TRANSITIONED
  }
  return (
    isFetchBaseError(error) &&
    error.status === CUSTOM_ERROR_STATUS &&
    error.data === SESSION_ERROR_KEY.SESSION_TRANSITIONED
  )
}

const isSessionError = (error?: unknown): error is SessionError => {
  const sessionError = error as SessionError
  return Boolean(sessionError?.errorCode) || Boolean(sessionError?.errorKey)
}

export const isFetchBaseError = (error?: unknown): error is FetchBaseQueryError => {
  const fetchError = error as FetchBaseQueryError
  return Boolean(fetchError?.status)
}

export const isUnauthorizedError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 401
}

export const isResourceNotFoundError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 404
}

export const isConflictError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 409
}

export const isInternalServerError = (error?: unknown): boolean => {
  return isFetchBaseError(error) && error.status === 500
}

const isFetchBaseErrorData = (error?: unknown): error is FetchErrorData => {
  return isFetchBaseError(error)
}

export const getErrorMessage = (error?: unknown): string | undefined => {
  if (isFetchBaseErrorData(error)) {
    if (typeof error.error === 'string') return error.error
    if (typeof error.data === 'string') return error.data

    if (typeof error.data === 'object') {
      if (typeof error.data?.error === 'string') return error.data.error
      if (typeof error.data?.message === 'string') return error.data.message
      try {
        return JSON.stringify(error.data)
      } catch (e) {
        return undefined
      }
    }
  }

  if (typeof error === 'string') {
    return error
  }
}

export const createSessionExpiredFetchError = (error: string): FetchBaseQueryError => {
  return {
    status: CUSTOM_ERROR_STATUS,
    data: SESSION_ERROR_KEY.SESSION_EXPIRED,
    error,
  }
}

export const createSessionTransitionedFetchError = (error: string): FetchBaseQueryError => {
  return {
    status: CUSTOM_ERROR_STATUS,
    data: SESSION_ERROR_KEY.SESSION_TRANSITIONED,
    error,
  }
}
